import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../utils/apiUrl';

const PrivacyPolicy = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [footerCopyright, setFooterCopyright] = useState('Copyright © 2026 Blue API, All rights reserved.');

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 获取隐私政策内容
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(getApiUrl('/api/homepage-config'));
        const data = await response.json();
        if (data.code === 0 && data.data) {
          setContent(data.data.privacy_policy_content || '');
          if (data.data.footer_copyright) {
            setFooterCopyright(data.data.footer_copyright);
          }
        }
      } catch (error) {
        console.log('获取隐私政策失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // 默认内容
  const defaultContent = lang === 'zh' ? `
    <h1>隐私政策</h1>
    <p>最后更新日期：2026年1月1日</p>
    <h2>1. 信息收集</h2>
    <p>我们收集您在使用我们服务时提供的信息，包括但不限于：</p>
    <ul>
      <li>账户信息（邮箱、密码等）</li>
      <li>使用数据（API调用记录、消费记录等）</li>
      <li>设备信息（IP地址、浏览器类型等）</li>
    </ul>
    <h2>2. 信息使用</h2>
    <p>我们使用收集的信息用于：</p>
    <ul>
      <li>提供和维护我们的服务</li>
      <li>处理您的订单和支付</li>
      <li>改进我们的服务质量</li>
      <li>与您沟通服务相关事宜</li>
    </ul>
    <h2>3. 信息保护</h2>
    <p>我们采取适当的安全措施保护您的个人信息，防止未经授权的访问、使用或泄露。</p>
    <h2>4. 信息共享</h2>
    <p>我们不会将您的个人信息出售给第三方。仅在以下情况下可能共享信息：</p>
    <ul>
      <li>获得您的明确同意</li>
      <li>遵守法律法规要求</li>
      <li>保护我们的合法权益</li>
    </ul>
    <h2>5. 您的权利</h2>
    <p>您有权访问、更正或删除您的个人信息。如需行使这些权利，请联系我们。</p>
    <h2>6. 联系我们</h2>
    <p>如有任何隐私相关问题，请通过以下方式联系我们：</p>
    <p>邮箱：privacy@example.com</p>
  ` : `
    <h1>Privacy Policy</h1>
    <p>Last updated: January 1, 2026</p>
    <h2>1. Information Collection</h2>
    <p>We collect information you provide when using our services, including but not limited to:</p>
    <ul>
      <li>Account information (email, password, etc.)</li>
      <li>Usage data (API call records, consumption records, etc.)</li>
      <li>Device information (IP address, browser type, etc.)</li>
    </ul>
    <h2>2. Information Use</h2>
    <p>We use the collected information to:</p>
    <ul>
      <li>Provide and maintain our services</li>
      <li>Process your orders and payments</li>
      <li>Improve our service quality</li>
      <li>Communicate with you about service-related matters</li>
    </ul>
    <h2>3. Information Protection</h2>
    <p>We take appropriate security measures to protect your personal information from unauthorized access, use, or disclosure.</p>
    <h2>4. Information Sharing</h2>
    <p>We do not sell your personal information to third parties. We may share information only in the following circumstances:</p>
    <ul>
      <li>With your explicit consent</li>
      <li>To comply with legal requirements</li>
      <li>To protect our legitimate rights and interests</li>
    </ul>
    <h2>5. Your Rights</h2>
    <p>You have the right to access, correct, or delete your personal information. To exercise these rights, please contact us.</p>
    <h2>6. Contact Us</h2>
    <p>For any privacy-related questions, please contact us:</p>
    <p>Email: privacy@example.com</p>
  `;

  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="legal-logo">
            <span>Blue API</span>
          </Link>
          <div className="legal-nav-links">
            <Link to="/">{t('返回首页', 'Back to Home')}</Link>
          </div>
        </div>
      </nav>

      <main className="legal-content">
        <div className="legal-container">
          {loading ? (
            <div className="legal-loading">{t('加载中...', 'Loading...')}</div>
          ) : content ? (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: defaultContent }} />
          )}
        </div>
      </main>

      <footer className="legal-footer">
        <p>{footerCopyright}</p>
      </footer>

      <style>{`
        .legal-page {
          min-height: 100vh;
          background: #f8fafc;
        }
        .legal-nav {
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          padding: 16px 0;
        }
        .legal-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .legal-logo {
          font-size: 20px;
          font-weight: 600;
          color: #1677ff;
          text-decoration: none;
        }
        .legal-nav-links a {
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
        }
        .legal-nav-links a:hover {
          color: #1677ff;
        }
        .legal-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 48px 24px;
        }
        .legal-container {
          background: #fff;
          border-radius: 12px;
          padding: 48px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .legal-html h1 {
          font-size: 28px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 2px solid #e5e7eb;
        }
        .legal-html h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-top: 32px;
          margin-bottom: 16px;
        }
        .legal-html p {
          font-size: 15px;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 16px;
        }
        .legal-html ul {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .legal-html li {
          font-size: 15px;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 8px;
        }
        .legal-loading {
          text-align: center;
          padding: 48px;
          color: #6b7280;
        }
        .legal-footer {
          text-align: center;
          padding: 24px;
          color: #9ca3af;
          font-size: 14px;
          border-top: 1px solid #e5e7eb;
        }
        @media (max-width: 640px) {
          .legal-container {
            padding: 24px;
          }
          .legal-html h1 {
            font-size: 24px;
          }
          .legal-html h2 {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;
